import { useEffect, useId, useRef, useState, useTransition, type KeyboardEvent } from 'react';
import { Link } from 'react-router';
import { AnimatePresence, motion } from 'framer-motion';

import { cn } from '@psspl/utils';

import { useAuth } from '../../auth/auth-context';
import { MOTION_DURATION, MOTION_EASE } from '../../lib/motion';
import { ROLE_LABEL } from '../../lib/roles';
import { ROUTES } from '../../lib/routes';
import type { SessionUser } from '../../types';
import { LOGOUT_ICON, PROFILE_ICON } from './navigation-items';

type NameParts = Pick<SessionUser, 'firstName' | 'lastName'>;

/** Initials from the user's own name. The contract carries no avatar, so none is invented. */
export function initialsOf(user: NameParts): string {
  return [user.firstName, user.lastName]
    .map((part) => part.trim().charAt(0).toUpperCase())
    .join('');
}

export function UserAvatar({
  user,
  size = 'sm',
}: {
  readonly user: NameParts;
  readonly size?: 'sm' | 'lg';
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full border border-brand-500/40',
        'bg-brand-500/10 font-semibold tracking-tight text-brand-200',
        size === 'sm' ? 'size-8 text-xs' : 'size-20 text-xl',
      )}
    >
      {initialsOf(user)}
    </span>
  );
}

/**
 * Header account menu: who is signed in, in what role, with Profile and Sign out.
 *
 * A disclosure-style menu button (WAI-ARIA menu pattern): Enter, Space or ArrowDown
 * opens it and focuses the first item; arrow keys move between items; Escape and
 * Tab close it, and Escape returns focus to the button. A click outside closes it.
 * The role shown is a label from the session, not a permission check.
 */
export function UserMenu({ user }: { readonly user: SessionUser }) {
  const { logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isSigningOut, startSignOut] = useTransition();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const items = (): HTMLElement[] =>
    Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);

  const close = (restoreFocus: boolean): void => {
    setIsOpen(false);

    if (restoreFocus) {
      buttonRef.current?.focus();
    }
  };

  // Focus the first item when the menu opens.
  useEffect(() => {
    if (isOpen) {
      items()[0]?.focus();
    }
  }, [isOpen]);

  // Close on a pointer press anywhere outside the button and the menu.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node | null;

      if (
        target !== null &&
        !menuRef.current?.contains(target) &&
        !buttonRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', onPointerDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [isOpen]);

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const all = items();
    const index = all.indexOf(document.activeElement as HTMLElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        all[(index + 1) % all.length]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        all[(index - 1 + all.length) % all.length]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        all[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        all.at(-1)?.focus();
        break;
      case 'Escape':
        event.preventDefault();
        close(true);
        break;
      case 'Tab':
        close(false);
        break;
      default:
        break;
    }
  };

  const itemClass =
    'flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm text-ink-secondary ' +
    'transition-colors duration-150 hover:bg-surface-hover hover:text-ink-primary ' +
    'focus-visible:bg-surface-hover focus-visible:text-ink-primary disabled:opacity-55';

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        onClick={() => {
          setIsOpen((open) => !open);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !isOpen) {
            event.preventDefault();
            setIsOpen(true);
          }
        }}
        className="flex min-w-0 items-center gap-2.5 rounded-md py-1 pr-2 pl-1 transition-colors duration-150 hover:bg-surface-raised"
      >
        <UserAvatar user={user} />
        <span className="hidden min-w-0 flex-col text-left leading-tight sm:flex">
          <span className="max-w-[16ch] truncate text-sm text-ink-primary">{user.fullName}</span>
          <span className="text-[0.6875rem] text-ink-muted">{ROLE_LABEL[user.role]}</span>
        </span>
        <svg
          viewBox="0 0 20 20"
          className={cn(
            'size-4 shrink-0 text-ink-muted transition-transform duration-150',
            isOpen && 'rotate-180',
          )}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <path d="m6 8 4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="sr-only">
          {`Account menu for ${user.fullName}, ${ROLE_LABEL[user.role]}`}
        </span>
      </button>

      <AnimatePresence>
        {isOpen ? (
          <motion.div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label="Account"
            onKeyDown={onMenuKeyDown}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: MOTION_DURATION, ease: MOTION_EASE }}
            className="absolute right-0 z-40 mt-2 w-64 rounded-lg border border-line-strong bg-surface-overlay p-1.5 shadow-popover"
          >
            <div className="flex flex-col gap-0.5 border-b border-line-subtle px-3 pt-2 pb-3">
              <span className="truncate text-sm font-medium text-ink-primary">{user.fullName}</span>
              <span className="truncate text-xs text-ink-muted">{user.email}</span>
              <span className="text-xs text-ink-muted">{ROLE_LABEL[user.role]}</span>
            </div>
            <div className="flex flex-col gap-0.5 pt-1.5">
              <Link
                to={ROUTES.profile}
                role="menuitem"
                tabIndex={-1}
                className={itemClass}
                onClick={() => {
                  setIsOpen(false);
                }}
              >
                {PROFILE_ICON}
                Profile
              </Link>
              <button
                type="button"
                role="menuitem"
                tabIndex={-1}
                disabled={isSigningOut}
                className={itemClass}
                onClick={() => {
                  // The route guard sends the user to sign-in once the session ends.
                  startSignOut(async () => {
                    await logout();
                  });
                }}
              >
                {LOGOUT_ICON}
                {isSigningOut ? 'Logging out…' : 'Logout'}
              </button>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
